/**
 * Trang Nộp đơn khởi kiện (Smart Filing) – wizard từng bước, đáp ứng đặc tả.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { ArrowBack, ArrowForward, Send, VerifiedUser, UploadFile, AutoAwesome, Lightbulb } from '@mui/icons-material'
import { useAppSelector } from '../../hooks/redux'
import { useCreateCaseMutation } from '../../store/api/casesApi'
import { useCreateFilingMutation } from '../../store/api/filingsApi'

const steps = ['Phân luồng & Giá trị', 'Thông tin các bên', 'Nội dung & Chứng cứ', 'Xác nhận và nộp']

const DISPUTE_TYPES = [
  { value: 'ly_hon', label: 'Ly hôn' },
  { value: 'tranh_chap_tai_san', label: 'Tranh chấp tài sản chung' },
  { value: 'cap_duong_con', label: 'Cấp dưỡng, nuôi con' },
  { value: 'khac_hn_gd', label: 'Khác (Hôn nhân & Gia đình)' },
]

/** Giai đoạn 1 – AI gợi ý chứng cứ theo loại tranh chấp */
const EVIDENCE_SUGGESTIONS: Record<string, string[]> = {
  ly_hon: ['Giấy chứng nhận kết hôn', 'Đơn ly hôn (có chữ ký)', 'CMND/CCCD hai bên', 'Giấy tờ tài sản chung (nếu có tranh chấp)'],
  tranh_chap_tai_san: ['Đơn khởi kiện', 'Hợp đồng/chứng từ tài sản', 'Giấy chứng nhận quyền sở hữu', 'CMND/CCCD các bên'],
  cap_duong_con: ['Quyết định ly hôn (nếu có)', 'Giấy tờ thu nhập', 'Chứng từ chi phí nuôi con', 'CMND/CCCD'],
  khac_hn_gd: ['Đơn khởi kiện', 'CMND/CCCD', 'Các chứng cứ liên quan theo nội dung yêu cầu'],
}

const CONTENT_SUGGESTIONS: Record<string, { subject: string; reason: string }> = {
  ly_hon: { subject: 'Yêu cầu giải quyết ly hôn', reason: 'Hai bên không thể tiếp tục duy trì hôn nhân, đã thỏa thuận (hoặc chưa) về con chung và tài sản. Kính đề nghị Tòa án xem xét giải quyết.' },
  tranh_chap_tai_san: { subject: 'Tranh chấp phân chia tài sản chung', reason: 'Các bên không thống nhất về việc phân chia tài sản chung. Kính đề nghị Tòa án phân chia theo quy định pháp luật.' },
  cap_duong_con: { subject: 'Yêu cầu cấp dưỡng nuôi con', reason: 'Con chung đang ở với bên nguyên đơn. Đề nghị Tòa án buộc bị đơn thực hiện nghĩa vụ cấp dưỡng theo mức thu nhập và nhu cầu của con.' },
  khac_hn_gd: { subject: 'Vấn đề cần giải quyết', reason: 'Trình bày ngắn gọn lý do, tình tiết và yêu cầu cụ thể đối với Tòa án.' },
}

/** Format số tiền có dấu phẩy nghìn (150000000 → "150,000,000"). */
function formatClaimValueWithCommas(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (digits === '') return ''
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** Parse chuỗi đã format (có dấu phẩy) thành number. */
function parseClaimValueFormatted(formatted: string): number {
  const digits = formatted.replace(/\D/g, '')
  return digits === '' ? 0 : parseInt(digits, 10) || 0
}

export default function NewCasePage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const user = useAppSelector((s) => s.auth.user)
  const [activeStep, setActiveStep] = useState(0)
  const [track, setTrack] = useState<'A' | 'B'>('A')
  const [claimValue, setClaimValue] = useState<string>('150,000,000')
  const [defendantName, setDefendantName] = useState('Nguyễn Văn An')
  const [defendantContact, setDefendantContact] = useState('Số 10 đường Lê Lợi, quận 1, TP.HCM · 0901234567')
  const [disputeType, setDisputeType] = useState('ly_hon')
  const [subject, setSubject] = useState('Yêu cầu giải quyết ly hôn')
  const [reason, setReason] = useState('Hai bên không thể tiếp tục duy trì hôn nhân, đã thỏa thuận (hoặc chưa) về con chung và tài sản. Kính đề nghị Tòa án xem xét giải quyết.')
  const [evidenceNames, setEvidenceNames] = useState<string[]>(['Giấy chứng nhận kết hôn', 'Đơn ly hôn (bản scan).pdf'])
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [createCase, { isLoading: creatingCase }] = useCreateCaseMutation()
  const [createFiling, { isLoading: creatingFiling }] = useCreateFilingMutation()

  const handleNext = () => setActiveStep((s) => s + 1)
  const handleBack = () => setActiveStep((s) => s - 1)

  const claimNum = parseClaimValueFormatted(claimValue)
  const formErrorTrackA = track === 'A' && claimNum > 200_000_000
  const formErrorValue = claimNum < 0

  const handleSubmit = async () => {
    if (!user) return
    try {
      const newCase = await createCase({
        track,
        category: 'marriage_family',
        claim_value_vnd: claimNum,
        plaintiff_id: user.id,
        defendant_id: 'defendant-placeholder',
        plaintiff_name: user.full_name || user.username || undefined,
        defendant_name: defendantName.trim() || undefined,
      }).unwrap()
      await createFiling({
        case_id: newCase.id,
        type: 'complaint',
        form_data: {
          subject,
          reason,
          dispute_type: disputeType,
          defendant_name: defendantName,
          defendant_contact: defendantContact,
          evidence_count: evidenceNames.length,
        },
      }).unwrap()
      setSubmitSuccess(true)
      setTimeout(() => navigate(`/cases/${newCase.id}`), 1500)
    } catch (e) {
      console.error(e)
    }
  }

  const handleClaimValueChange = (raw: string) => setClaimValue(formatClaimValueWithCommas(raw))

  const isStepValid = () => {
    if (activeStep === 0) return track && claimValue !== '' && !formErrorValue && (track === 'B' || !formErrorTrackA)
    if (activeStep === 1) return defendantName.trim().length > 0 && disputeType !== ''
    if (activeStep === 2) return subject.trim().length > 0 && reason.trim().length > 0
    return true
  }

  const addEvidencePlaceholder = () => setEvidenceNames((prev) => [...prev, `Chứng cứ_${prev.length + 1}.pdf`])
  const suggestedEvidence = disputeType ? EVIDENCE_SUGGESTIONS[disputeType] ?? [] : []
  const addSuggestedEvidence = (name: string) => setEvidenceNames((prev) => (prev.includes(name) ? prev : [...prev, name]))
  const fillFromSuggestion = () => {
    if (!disputeType) return
    const s = CONTENT_SUGGESTIONS[disputeType]
    if (s) {
      setSubject(s.subject)
      setReason(s.reason)
    }
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/cases')} sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
        Quay lại
      </Button>

      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
        Nộp đơn khởi kiện (Smart Filing)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
        Hướng dẫn từng bước · Hệ thống kiểm tra hình thức đơn và cảnh báo lỗi trước khi gửi
      </Typography>

      <Alert severity="info" icon={<VerifiedUser />} sx={{ mb: { xs: 2, sm: 3 }, '& .MuiAlert-message': { fontSize: { xs: '0.8125rem', sm: '0.875rem' } } }}>
        <strong>Xác thực danh tính (e-KYC):</strong> Bước xác thực sẽ được áp dụng khi đăng ký dịch vụ đầy đủ (2FA, VNeID).
      </Alert>

      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stepper activeStep={activeStep} sx={{ mb: { xs: 2, sm: 4 } }} orientation={isMobile ? 'vertical' : 'horizontal'}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: { xs: '0.8125rem', sm: '0.875rem' } } }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 520 }}>
              <FormControl fullWidth>
                <InputLabel>Phân luồng xử lý</InputLabel>
                <Select value={track} label="Phân luồng xử lý" onChange={(e) => setTrack(e.target.value as 'A' | 'B')}>
                  <MenuItem value="A">Track A – Fully Online (giá trị &lt; 200 triệu)</MenuItem>
                  <MenuItem value="B">Track B – Hybrid</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Giá trị tranh chấp (VNĐ)"
                type="text"
                inputMode="numeric"
                placeholder="VD: 150,000,000"
                value={claimValue}
                onChange={(e) => handleClaimValueChange(e.target.value)}
                error={formErrorValue || formErrorTrackA}
                helperText={
                  formErrorValue
                    ? 'Giá trị không hợp lệ.'
                    : formErrorTrackA
                      ? 'Track A yêu cầu giá trị < 200 triệu. Vui lòng chọn Track B hoặc điều chỉnh.'
                      : undefined
                }
                InputProps={{ endAdornment: <InputAdornment position="end">đ</InputAdornment> }}
              />
              {track === 'A' && claimValue && claimNum > 0 && claimNum <= 200_000_000 && (
                <Alert severity="success">Đủ điều kiện Track A (Fully Online).</Alert>
              )}
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Nguyên đơn (bạn)</Typography>
              <TextField fullWidth size="small" label="Họ tên" value={user?.full_name || user?.username || '–'} disabled />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>Bị đơn</Typography>
              <TextField
                fullWidth
                label="Họ tên bị đơn"
                value={defendantName}
                onChange={(e) => setDefendantName(e.target.value)}
                required
                placeholder="Nhập họ tên đầy đủ"
              />
              <TextField
                fullWidth
                label="Địa chỉ / Số điện thoại liên hệ"
                value={defendantContact}
                onChange={(e) => setDefendantContact(e.target.value)}
                placeholder="Để Tòa án tống đạt"
              />
              <FormControl fullWidth required>
                <InputLabel>Loại tranh chấp (Hôn nhân & Gia đình)</InputLabel>
                <Select value={disputeType} label="Loại tranh chấp (Hôn nhân & Gia đình)" onChange={(e) => setDisputeType(e.target.value)}>
                  {DISPUTE_TYPES.map((d) => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
              <Alert severity="info" icon={<Lightbulb />} sx={{ mb: 0.5 }}>
                <strong>Gợi ý nội dung:</strong> Dựa trên loại tranh chấp bạn đã chọn, hệ thống có thể điền sẵn nội dung mẫu để bạn chỉnh sửa.
              </Alert>
              <Button size="small" variant="outlined" startIcon={<AutoAwesome />} onClick={fillFromSuggestion} disabled={!disputeType} sx={{ alignSelf: 'flex-start' }}>
                Điền nhanh theo gợi ý AI
              </Button>
              <TextField
                fullWidth
                label="Vấn đề cần giải quyết"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Ví dụ: Ly hôn, Tranh chấp tài sản chung"
                required
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Nội dung tóm tắt"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Trình bày ngắn gọn lý do, tình tiết..."
                required
              />
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UploadFile fontSize="small" /> Chứng cứ đính kèm
                </Typography>
                {disputeType && suggestedEvidence.length > 0 && (
                  <Alert icon={<AutoAwesome />} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Gợi ý chứng cứ cần nộp (theo loại vụ):</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2, '& li': { py: 0.25 } }}>
                      {suggestedEvidence.map((name) => (
                        <li key={name}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {name}
                            <Button size="small" variant="text" onClick={() => addSuggestedEvidence(name)} disabled={evidenceNames.includes(name)}>
                              {evidenceNames.includes(name) ? 'Đã thêm' : '+ Thêm'}
                            </Button>
                          </Box>
                        </li>
                      ))}
                    </Box>
                  </Alert>
                )}
                <Alert severity="info" sx={{ mb: 1 }}>Tài liệu đính kèm sẽ được lưu và gửi kèm theo đơn. Hỗ trợ định dạng PDF, hình ảnh; dung lượng tối đa theo quy định Tòa án.</Alert>
                {evidenceNames.length > 0 && (
                  <List dense>
                    {evidenceNames.map((name, i) => (
                      <ListItem key={i}>
                        <ListItemIcon><UploadFile fontSize="small" /></ListItemIcon>
                        <ListItemText primary={name} />
                      </ListItem>
                    ))}
                  </List>
                )}
                <Button size="small" variant="outlined" startIcon={<UploadFile />} onClick={addEvidencePlaceholder} sx={{ mt: 1 }}>
                  Thêm tài liệu đính kèm
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={{ maxWidth: 560 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Tóm tắt đơn</Typography>
              <Typography variant="body2" paragraph>Phân luồng: <strong>{track === 'A' ? 'Fully Online' : 'Hybrid'}</strong></Typography>
              <Typography variant="body2" paragraph>Giá trị tranh chấp: <strong>{claimNum.toLocaleString('vi-VN')} VNĐ</strong></Typography>
              <Typography variant="body2" paragraph>Loại tranh chấp: <strong>{DISPUTE_TYPES.find((d) => d.value === disputeType)?.label || disputeType}</strong></Typography>
              <Typography variant="body2" paragraph>Bị đơn: <strong>{defendantName}</strong> {defendantContact && `· ${defendantContact}`}</Typography>
              <Typography variant="body2" paragraph>Vấn đề: <strong>{subject || '–'}</strong></Typography>
              <Typography variant="body2" paragraph>Nội dung: {reason || '–'}</Typography>
              <Typography variant="body2" paragraph>Chứng cứ đính kèm: <strong>{evidenceNames.length} file</strong></Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Sau khi nộp, Thư ký Tòa án sẽ kiểm tra hình thức và thông báo kết quả qua hệ thống.
              </Alert>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Xác thực danh tính (e-KYC) sẽ áp dụng khi đăng ký dịch vụ đầy đủ (2FA, VNeID).
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack} startIcon={<ArrowBack />}>Quay lại</Button>
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext} disabled={!isStepValid()} endIcon={<ArrowForward />}>Tiếp theo</Button>
            ) : (
              <Button variant="contained" onClick={handleSubmit} disabled={creatingCase || creatingFiling || !isStepValid()} startIcon={<Send />}>
                {creatingCase || creatingFiling ? 'Đang gửi...' : 'Gửi đơn'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Snackbar open={submitSuccess} autoHideDuration={4000} onClose={() => setSubmitSuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSubmitSuccess(false)}>
          Đơn đã được gửi thành công. Thư ký Tòa án sẽ kiểm tra hình thức và thông báo kết quả.
        </Alert>
      </Snackbar>
    </Box>
  )
}
